import PoweredByApruv from "../../components/common/PoweredByApruv";
import { CompletedServiceSummary } from "./CompletedServiceSummary";
import { evaluateFlagGuards } from '@apruv/survey-contract/flags';
import { FeedbackRecoveryContent } from "./FeedbackRecoveryContent";
import { FeedbackConfirmationContent } from "./FeedbackConfirmationContent";
import { FeedbackFlowSheet } from "./FeedbackFlowSheet";
import { FeedbackRecoveryFields } from "./FeedbackRecoveryFields";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { CompiledSurvey } from "@apruv/survey-contract/compiler";
import { resolveLocalizedText } from "@apruv/survey-contract/localization";
import type { CXPublicSurveyResponse } from "../../services/cxPublicSurveyService";
import CompanyBrandProvider from "../CompanyBrandProvider";
import { flowComponentRegistry } from "../componentRegistry";
import type {
  FeedbackFormComponentConfig,
  FlowComponentConfig,
  FlowValues,
} from "../types";
import {
  canonicalAnswerContext,
  canonicalSurveyContext,
} from "./canonicalSurveyContext";
import { canonicalSurveyToFlow } from "./canonicalSurveyFlow";
import { createCanonicalFlowDriver } from "./canonicalFlowDriver";
import {
  useCanonicalSurveySession,
  type CanonicalSessionCallbacks,
} from "./useCanonicalSurveySession";
import { FeedbackSurveyShell, FeedbackSurveyCard } from "./FeedbackSurveyShell";
import { FeedbackSurveyHeader } from "./FeedbackSurveyHeader";
import { FeedbackShellMedia } from "./FeedbackShellMedia";
import HtmlText from "../components/HtmlText";
import { surveyHtmlText } from "../sanitizeHtml";
import { resolveSurveyTemplate } from "../customerSurveyFlow";
import { canonicalExecutionPolicy } from "./canonicalSurveyContext";
import FeedbackTippingCard, {
  createTipPaymentRecovery,
  type TipPaymentRecovery,
} from "./FeedbackTippingCard";
import FeedbackNpsRating from "./FeedbackNpsRating";
import FeedbackActionButton from "./FeedbackActionButton";
import FeedbackReviewSelection from "./FeedbackReviewSelection";
import FeedbackCsatRating, { FeedbackCsatFaceIcon } from "./FeedbackCsatRating";
import FeedbackTechnicianNotes from "./FeedbackTechnicianNotes";
import { useSurveyFavicon } from "./useSurveyFavicon";
import { FeedbackAcknowledgedSummary } from "./FeedbackAcknowledgedSummary";
import { getRatingState } from "../components/rating/ratingUtils";
import { paymentCurrency } from "./paymentCurrency";
import { useCompactProofHero } from "./useCompactProofHero";

const commandKey = (component: FlowComponentConfig) =>
  `${component.id}:${component.type === "actionButton" ? (component.canonicalActionId ?? "") : ""}`;

const retiredReviewHeading =
  "Thanks for taking the time to share your experience";
const retainedReviewPrompt = "Mind leaving us a quick review?";

interface Props extends CanonicalSessionCallbacks {
  onReviewCTAVisible?: (provider: string) => Promise<void>;
  compiled: CompiledSurvey;
  response: CXPublicSurveyResponse;
  initialValues: FlowValues;
  onComplete?: (values: FlowValues) => void;
  onPreviewDestination?: (
    destination: import("@apruv/survey-contract/compiler").SurveyDestination,
  ) => void;
}
/** Separate canonical state machine: legacy array routing and persistence inference never run here. */
function InlineServiceProofMedia(props: Omit<React.ComponentProps<typeof FeedbackShellMedia>, "bottomInset" | "onMinimumHeightChange">) {
  const [height, setHeight] = useState(316);
  return <Box sx={{ position: "relative", height: Math.max(316, height), overflow: "hidden", borderRadius: 2 }}>
    <FeedbackShellMedia {...props} bottomInset={0} onMinimumHeightChange={setHeight} />
  </Box>;
}

export default function CanonicalSurveyRenderer({
  compiled,
  response,
  initialValues,
  onComplete,
  onPreviewDestination,
  onReviewCTAVisible,
  ...callbacks
}: Props) {
  const paymentRecoveries = useRef(
    new Map<string, TipPaymentRecovery>(),
  ).current;
  const [simulatedTips, setSimulatedTips] = useState<Record<string, number>>({});
  const [reviewRevealed, setReviewRevealed] = useState<Record<string, boolean>>({});
  const revealedReviewRef = useRef<HTMLDivElement>(null);
  const identity = compiled.snapshot.identity;
  const paymentScope = JSON.stringify([
    initialValues.cxSurveyCode,
    response.executionStepId,
    identity.organizationId,
    identity.experienceId,
    identity.experienceVersionId,
    identity.draftRevision,
  ]);
  const [minimumMediaHeight, setMinimumMediaHeight] = useState(280);
  const driver = useMemo(
    () => createCanonicalFlowDriver(compiled, response),
    [compiled, response],
  );
  const session = useCanonicalSurveySession(driver, initialValues, callbacks);
  useEffect(() => {
    if (canonicalExecutionPolicy(response).preview)
      onPreviewDestination?.(session.destination);
  }, [response, session.destination, onPreviewDestination]);
  const [showRevisitTip, setShowRevisitTip] = useState(false);
  const revisitMessage = driver.revisit?.message && !showRevisitTip;
  const base = useMemo(
    () => canonicalSurveyContext(compiled, response),
    [compiled, response],
  );
  const context = canonicalAnswerContext(
    base,
    session.values,
    session.ratingComponentId,
    compiled,
  );
  const flow = canonicalSurveyToFlow(
    compiled,
    response,
    context,
    session.ratingComponentId,
  );
  useSurveyFavicon(flow.brand.faviconUrl);
  const configuration = compiled.snapshot.configuration;
  // Tenant-authored opt-in for the NPS → tip → review experiment. The text
  // map is preserved by existing survey-contract readers during rollout.
  const tipFirstReviewEnabled = configuration.content.text['experiment.tipFirstReview'] === 'true';
  const localizedOverride = (path: string) => {
    const localization = configuration.localization;
    const locale =
      typeof base.locale === "string" &&
      localization.supportedLocales.includes(base.locale)
        ? base.locale
        : localization.defaultLocale;
    return (
      localization.translations[locale]?.[`configuration/${path}`] ??
      localization.translations[localization.defaultLocale]?.[
        `configuration/${path}`
      ]
    );
  };
  const localizedCopy = (path: string, fallback: string) => {
    return resolveSurveyTemplate(
      localizedOverride(path) ?? fallback,
      response,
      configuration.branding,
      configuration.content.variableFallbacks,
    );
  };
  const resolvedContinueLabel = Object.prototype.hasOwnProperty.call(
    configuration.content.text,
    "survey.continue",
  )
    ? resolveSurveyTemplate(
        resolveLocalizedText(
          compiled.snapshot,
          typeof base.locale === "string" &&
            configuration.localization.supportedLocales.includes(base.locale)
            ? base.locale
            : configuration.localization.defaultLocale,
          "text/survey.continue/value",
        ),
        response,
        configuration.branding,
        configuration.content.variableFallbacks,
      )
    : "Continue";
  const continueLabel = resolvedContinueLabel.trim() || "Continue";
  const policy = canonicalExecutionPolicy(response);
  const blockNativeNavigation = (event: React.SyntheticEvent) => {
    if (
      (!policy.external || session.pending) &&
      event.target instanceof Element &&
      event.target.closest("a[href], area[href], form")
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const sheetContainer = useRef<HTMLDivElement>(null);
  const screenId =
    session.destination.kind === "screen"
      ? session.destination.screenId
      : undefined;
  const sourceScreen = flow.screens.find((item) => item.id === screenId);
  const screen = sourceScreen
    ? {
        ...sourceScreen,
        components: sourceScreen.components.flatMap((component) =>
          component.type === "actionButton" &&
          component.canonicalActions?.length
            ? component.canonicalActions.map((action) => ({
                ...action,
                canonicalActions: undefined,
              }))
            : [component],
        ),
      }
    : undefined;
  const feedbackForms =
    screen?.components.filter(
      (component): component is FeedbackFormComponentConfig =>
        component.type === "feedbackForm",
    ) ?? [];
  const isFeedbackSheet =
    !driver.revisit &&
    feedbackForms.length > 0 &&
    !screen?.components.some((component) =>
      ["npsForm", "csatForm", "tipping", "finixTipping"].includes(
        component.type,
      ),
    );
  const isReviewSheet =
    !driver.revisit && screen?.presentation === "recovery_review";
  const isSheet = isFeedbackSheet || isReviewSheet;
  const sheetHeading = screen?.components.find(
    (component) => component.type === "h1",
  );
  const ratingComponent = screen?.components.find(
    (component) =>
      component.type === "npsForm" || component.type === "csatForm",
  );
  const subtitleId = compiled.snapshot.screens
    .find((item) => item.id === screenId)
    ?.components.find((item) => item.type === "subheader")?.id;
  const sheetSubtitle = screen?.components.find(
    (component) => component.id === subtitleId,
  );
  const feedbackSheetTitle = isFeedbackSheet
    ? ([
        localizedCopy("feedback/title", ""),
        sheetHeading?.type === "h1" ? sheetHeading.text : "",
        resolveSurveyTemplate(
          configuration.feedback.title,
          response,
          configuration.branding,
          configuration.content.variableFallbacks,
        ),
      ].find((title) => Boolean(surveyHtmlText(title))) ?? "Feedback")
    : undefined;
  const authoredScreen = compiled.snapshot.screens.find(
    (item) => item.id === screenId,
  );
  const tippingComponent = screen?.components.find(
    (component) =>
      component.type === "tipping" || component.type === "finixTipping",
  );
  const redundantTippingNavigationIds = new Set(
    tippingComponent
      ? (authoredScreen?.components ?? [])
          .filter((component) => {
            const legacyLabel =
              component.type === "primary_button"
                ? "Continue"
                : component.type === "secondary_button"
                  ? "Skip tipping"
                  : undefined;
            return (
              legacyLabel !== undefined &&
              component.content === legacyLabel &&
              component.actionIds.length === 0 &&
              component.config.buttonAction === "continue" &&
              component.config.routeTargetPageKey === "positive"
            );
          })
          .map((component) => component.id)
      : [],
  );
  const feedbackSkipButtons =
    screen?.components.filter(
      (component) =>
        component.type === "actionButton" &&
        component.action === "continue" &&
        (!component.canonicalActionId ||
          compiled.snapshot.actions.find(
            (action) => action.id === component.canonicalActionId,
          )?.type === "continue") &&
        authoredScreen?.components.find((source) => source.id === component.id)
          ?.type === "secondary_button",
    ) ?? [];
  const feedbackSubmitButtons =
    screen?.components.filter(
      (component) =>
        component.type === "actionButton" &&
        component.action === "continue" &&
        (compiled.snapshot.actions.find(
          (action) => action.id === component.canonicalActionId,
        )?.type === "submit" ||
          authoredScreen?.components.find(
            (source) => source.id === component.id,
          )?.type === "primary_button"),
    ) ?? [];
  const feedbackActionLabel = (
    kind: "skip" | "submit",
    authoredLabel: string,
    count: number,
  ) => {
    // A single global label cannot distinguish multiple independently owned commands.
    const candidate =
      count === 1 ? localizedCopy(`feedback/${kind}Label`, "") : authoredLabel;
    if (candidate.trim()) return candidate;
    if (authoredLabel.trim()) return authoredLabel;
    return kind === "skip" ? "Skip" : "Submit";
  };
  const feedbackActionIds = new Set(
    [...feedbackSkipButtons, ...feedbackSubmitButtons].map(commandKey),
  );
  const terminal = session.destination.kind === "terminal";
  const completedVisit = useRef<number | null>(null);
  useEffect(() => {
    if (
      terminal &&
      !revisitMessage &&
      completedVisit.current !== session.visitId
    ) {
      completedVisit.current = session.visitId;
      onComplete?.(session.values);
    }
  }, [terminal, revisitMessage, session.visitId, session.values, onComplete]);
  const renderBlock = (config: FlowComponentConfig, feedbackSkip?: boolean) => {
    if (!screen) return null;
    const screenId = screen.id;
    const resolvedConfig = config;
    const feedbackBoundary =
      feedbackSkip ??
      (isFeedbackSheet &&
      config.type !== "backButton" &&
      !(
        config.type === "actionButton" &&
        ["back", "edit_response"].includes(config.action)
      )
        ? false
        : undefined);
    const interact = (
      action?: Parameters<NonNullable<Props["onAction"]>>[0],
      submitted?: { id: string; value: unknown },
    ) =>
      session.interact(
        screenId,
        config.id,
        action,
        submitted,
        feedbackBoundary === undefined ? undefined : { skip: feedbackBoundary },
      );
    if (
      config.type === "backButton" &&
      driver.usesHistoryBack(screenId, config.id) &&
      !session.canGoBack
    )
      return null;
    if (config.type === "tipping" || config.type === "finixTipping") {
      try {
        // Registry payment blocks perform provider work internally. Validate the complete
        // owned interaction before mounting them, not after checkout has already started.
        const ownedIds =
          compiled.snapshot.screens
            .find((item) => item.id === screenId)
            ?.components.find((item) => item.id === config.id)?.actionIds ?? [];
        const tipActions = compiled.snapshot.actions.filter(
          (action) => ownedIds.includes(action.id) && action.type === "tip",
        );
        if (tipActions.length !== 1)
          throw new Error("Tipping is currently unavailable for this survey.");
        const preflight = (policy.preview || response.testMode)
          ? { guardDenied: !evaluateFlagGuards(compiled.snapshot, 'action', tipActions[0].id, context.resolvedFlags).allowed }
          : driver.resolve(
          screenId,
          session.values,
          session.ratingComponentId,
          config.id,
          {
            componentId: config.id,
            type: "tip",
          },
        );
        if (
          preflight.guardDenied &&
          screen.components.some(
            (component) =>
              component.id !== config.id && component.type === "actionButton",
          )
        )
          return null;
        if (preflight.guardDenied)
          return (
            <Button
              key={config.id}
              data-action-disabled={config.id}
              onClick={() =>
                session.interact(screenId, config.id, {
                  componentId: config.id,
                  type: "tip",
                })
              }
              disabled={session.pending}
            >
              Continue
            </Button>
          );
        // Finix configuration and recipient eligibility are checked by the
        // invite-bound API before the hosted form can submit a payment.
        if (config.type === "finixTipping" && !policy.preview && !response.testMode &&
            (!policy.tip || response.runtimeContext?.capabilities.legacyFinixEnabled !== true))
          throw new Error(
            "This payment method is not available for this survey.",
          );
      } catch (failure) {
        return (
          <Alert
            severity="error"
            key={config.id}
            data-payment-unavailable={config.id}
          >
            {failure instanceof Error
              ? failure.message
              : "This payment action is unavailable."}
            <Button
              onClick={() => session.interact(screenId)}
              disabled={session.pending}
            >
              Continue without tipping
            </Button>
          </Alert>
        );
      }
    }
    const Component = (
      resolvedConfig.type === "npsForm"
        ? FeedbackNpsRating
        : resolvedConfig.type === "csatForm"
          ? FeedbackCsatRating
          : resolvedConfig.type === "notes"
            ? FeedbackTechnicianNotes
            : resolvedConfig.type === "tipping"
              ? FeedbackTippingCard
              : resolvedConfig.type === "actionButton"
                ? resolvedConfig.action === "referral"
                  ? flowComponentRegistry.actionButton
                  : FeedbackActionButton
                : resolvedConfig.type === "reviewSelection"
                  ? FeedbackReviewSelection
                  : flowComponentRegistry[resolvedConfig.type]
    ) as React.FC<any> | undefined;
    if (!Component)
      return (
        <Alert severity="error" key={config.id}>
          This component is not supported.
        </Alert>
      );
    const recoveryKey = `${paymentScope}:${config.id}`;
    if ((config.type === "tipping" || config.type === "finixTipping") && !paymentRecoveries.has(recoveryKey)) {
      paymentRecoveries.set(recoveryKey, createTipPaymentRecovery());
    }
    const selectedTip = config.type === "tipping"
      ? session.values[config.id] as { amount?: number; customAmount?: string } | undefined
      : undefined;
    const selectedTipAmount = selectedTip?.customAmount?.trim()
      ? Number(selectedTip.customAmount)
      : selectedTip?.amount;
    const tipFirstAmountSelected = typeof selectedTipAmount === "number" &&
      Number.isFinite(selectedTipAmount) && selectedTipAmount > 0;
    return (
      <Box
        key={`${paymentScope}-${session.visitId}-${commandKey(config)}`}
        ref={resolvedConfig.type === "reviewSelection" && tipFirstReviewActive ? revealedReviewRef : undefined}
        tabIndex={resolvedConfig.type === "reviewSelection" && tipFirstReviewActive ? -1 : undefined}
        aria-label={resolvedConfig.type === "reviewSelection" && tipFirstReviewActive ? "Review options" : undefined}
        data-flow-component={resolvedConfig.type}
        data-component-id={config.id}
        data-action-id={
          config.type === "actionButton" ? config.canonicalActionId : undefined
        }
      >
        <Component
          onReviewCTAVisible={resolvedConfig.type === "reviewSelection" && policy.external ? onReviewCTAVisible : undefined}
          showCollapsedContinue={resolvedConfig.type === "tipping" ? (() => {
            try {
              const next = driver.resolve(screenId, session.values, session.ratingComponentId, config.id, {
                componentId: config.id, type: "continue", value: false,
              }).destination;
              return next.kind !== "screen" || next.screenId !== screenId;
            } catch {
              return true;
            }
          })() : undefined}
          amountSelectionFirst={resolvedConfig.type === "tipping" && tipFirstReviewActive && !reviewIsRevealed}
          onInlineSkip={resolvedConfig.type === "tipping" && tipFirstReviewActive && !reviewIsRevealed
              ? () => {
                setReviewRevealed((previous) => ({ ...previous, [screenId]: true }));
                if (!policy.preview && response.testMode !== true) {
                  void session.interact(screenId, config.id, { componentId: config.id, type: "continue", value: false });
                }
              }
            : undefined}
          onInlineTipConfirmed={resolvedConfig.type === "tipping" && tipFirstReviewActive
            ? () => setReviewRevealed((previous) => ({ ...previous, [screenId]: true }))
            : undefined}
          compactSummary={
            resolvedConfig.type === "reviewSelection" && showRatingSummary
          }
          pending={session.pending || (response.testMode === true && (
            resolvedConfig.type === "reviewSelection" ||
            (resolvedConfig.type === "actionButton" && ["contact", "callback", "review", "referral"].includes(resolvedConfig.action) &&
              (!resolvedConfig.canonicalActionId || evaluateFlagGuards(compiled.snapshot, "action", resolvedConfig.canonicalActionId, context.resolvedFlags).allowed))
          ))}
          ownedActions={compiled.snapshot.actions.filter((action) =>
            compiled.snapshot.screens
              .find((item) => item.id === screenId)
              ?.components.find((item) => item.id === config.id)
              ?.actionIds.includes(action.id),
          )}
          paymentRecovery={
            config.type === "tipping" || config.type === "finixTipping"
              ? paymentRecoveries.get(recoveryKey)
              : undefined
          }
          config={
            (policy.preview || response.testMode) && resolvedConfig.type === "tipping"
              ? { ...resolvedConfig, checkoutEnabled: false }
              : resolvedConfig
          }
          values={session.values}
          value={session.values[config.id]}
          isLastScreen={false}
          setValue={(id: string, value: unknown) => {
            if (id === config.id) session.setValue(screenId, id, value);
          }}
          submitValueAndGoNext={(id: string, value: unknown) =>
            interact(undefined, { id, value })
          }
          goNext={() => interact()}
          goBack={() => session.goBack(screenId, config.id)}
          goToScreen={() => interact()}
          onAction={interact}
          onInteract={interact}
        />
        {response.testMode === true && config.type === "reviewSelection" && (
          <Typography variant="caption" color="text.secondary">
            Test preview — review buttons are disabled. Live invitations use the assigned location’s review link.
          </Typography>
        )}
        {(policy.preview || response.testMode) && config.type === "tipping" &&
          (!tipFirstReviewActive || reviewIsRevealed || tipFirstAmountSelected) && (
          <Stack direction="row" spacing={1}>
            {policy.preview && <Button
              disabled={session.pending}
              onClick={() => {
                const selected = session.values[config.id] as { amount?: number; customAmount?: string } | undefined;
                const candidate = selected?.customAmount?.trim()
                  ? Number(selected.customAmount)
                  : selected?.amount;
                const amount = typeof candidate === "number" && Number.isFinite(candidate) && candidate > 0
                  ? candidate : config.amounts?.[0]?.value ?? 5;
                setSimulatedTips(previous => ({ ...previous, [recoveryKey]: amount }));
                session.interact(screenId, config.id, {
                  componentId: config.id,
                  type: "tip",
                  value: amount,
                });
              }}
            >
              Simulate successful tip
            </Button>}
            <Button
              disabled={session.pending}
              onClick={() =>
                session.interact(screenId, config.id, {
                  componentId: config.id,
                  type: "continue",
                  value: false,
                })
              }
            >
              Continue without tipping
            </Button>
          </Stack>
        )}
      </Box>
    );
  };
  const topBar = screen?.components.find(
    (component) => component.type === "topBar",
  );
  const header =
    topBar?.type === "topBar" ? (
      <FeedbackSurveyHeader
        compiled={compiled}
        response={response}
        logoUrl={topBar.logoUrl}
        logoLabel={topBar.logoLabel ?? flow.brand.companyName}
        disabled={session.pending}
        externalAllowed={policy.external}
        hideNotesAction={!driver.revisit}
        locale={base.locale}
      />
    ) : undefined;
  const positiveStep = Boolean(
    screen &&
    !driver.revisit &&
    (screen.presentation === "confirmation_card" ||
      screen.presentation === "recovery_review" ||
      compiled.snapshot.screens
        .find((item) => item.id === screen.id)
        ?.components.some((component) =>
          [
            "tip_section",
            "review_destinations",
            "comment_box",
            "tag_selector",
            "callback_cta",
          ].includes(component.type),
        )),
  );
  const ratingSource = flow.screens.find((item) =>
    item.components.some(
      (component) => component.id === session.ratingComponentId,
    ),
  );
  const ratingConfig = ratingSource?.components.find(
    (component) => component.id === session.ratingComponentId,
  );
  const acknowledgedRating = getRatingState(
    session.ratingComponentId
      ? session.acknowledgedAnswers[session.ratingComponentId]
      : undefined,
  );
  const showRatingSummary = Boolean(
    positiveStep &&
    ratingConfig &&
    acknowledgedRating.submitted &&
    acknowledgedRating.score !== null &&
    Number.isFinite(acknowledgedRating.score) &&
    !screen?.components.some(
      (component) => component.id === session.ratingComponentId,
    ),
  );
  const summaryEdit =
    screen && showRatingSummary
      ? driver.summaryEdit(screen.id, session.values, session.ratingComponentId)
      : undefined;
  const usesNeutralRatingSummary =
    configuration.summary.title === "Your feedback" &&
    configuration.summary.body === "";
  const ratingSummaryTitle = response.testMode === true
    ? "Rating selected"
    : usesNeutralRatingSummary
    ? "Rating submitted"
    : localizedCopy("summary/title", configuration.summary.title);
  const ratingSummaryBody = response.testMode === true
    ? "Preview only — feedback is not saved."
    : usesNeutralRatingSummary
    ? "Thanks, we've shared this with the team."
    : localizedCopy("summary/body", configuration.summary.body);
  const localizedSummaryEditLabel = localizedCopy(
    "summary/editLabel",
    configuration.summary.editLabel,
  );
  const ratingSummaryEditLabel =
    localizedSummaryEditLabel === "Edit response"
      ? "Edit"
      : localizedSummaryEditLabel;
  const csatSummaryOption =
    ratingConfig?.type === "csatForm" && acknowledgedRating.score !== null
      ? ratingConfig.options.find(
          (option) => option.value === acknowledgedRating.score,
        )
      : undefined;
  const ratingSummaryBadge =
    ratingConfig?.type === "csatForm" && acknowledgedRating.score !== null ? (
      <FeedbackCsatFaceIcon
        value={acknowledgedRating.score}
        size={34}
        label={csatSummaryOption?.label ?? "Selected satisfaction rating"}
      />
    ) : (
      String(acknowledgedRating.score ?? "")
    );
  const hideTippingBack = Boolean(tippingComponent && showRatingSummary);
  const confirmedTips =
    positiveStep
      ? flow.screens
          .flatMap((item) => item.components)
          .flatMap((component) => {
            if (component.type !== "tipping" && component.type !== "finixTipping") return [];
            const recoveryKey = `${paymentScope}:${component.id}`;
            const confirmed = policy.preview
              ? { amount: simulatedTips[recoveryKey] }
              : paymentRecoveries.get(recoveryKey)?.confirmedValue;
            return confirmed &&
              typeof confirmed.amount === "number" &&
              Number.isFinite(confirmed.amount) &&
              confirmed.amount > 0
              ? [{
                  component,
                  amount: confirmed.amount,
                  paymentCopy: component.type === "tipping" ? component.paymentCopy : undefined,
                  paymentLocale: component.type === "tipping" ? component.paymentLocale : undefined,
                }]
              : [];
          })
      : [];
  const currentProofs =
    screen?.components.filter(
      (component) => component.type === "proofOfServiceMedia",
    ) ?? [];
  const proofs = currentProofs.length
    ? currentProofs
    : showRatingSummary
      ? (ratingSource?.components.filter(
          (component) => component.type === "proofOfServiceMedia",
        ) ?? [])
      : [];
  // A proof block authored after the review CTA belongs in the card at that
  // position. Keep the existing hero behavior for leading and inherited proof.
  const reviewIndex = screen?.components.findIndex(component => component.type === "reviewSelection") ?? -1;
  const inlineProofIds = new Set(currentProofs.filter(component =>
    reviewIndex >= 0 && (screen?.components.findIndex(item => item.id === component.id) ?? -1) > reviewIndex
  ).map(component => component.id));
  const proof = proofs.find(component => !inlineProofIds.has(component.id));
  const renderCardBlock = (component: FlowComponentConfig) => {
    if (component.type !== "proofOfServiceMedia" || !inlineProofIds.has(component.id))
      return renderBlock(component);
    const items = component.items.filter(item => item.type !== "note");
    if (!items.length) return null;
    return <Stack key={component.id} spacing={1} data-proof-content={component.id}>
      {component.title && <HtmlText html={component.title} variant="subtitle2" />}
      {items.length > 1 && !configuration.hero.showProgress && !(configuration.gallery.enabled && configuration.hero.showCount)
        ? renderBlock({ ...component, items: items.map(item => ({ ...item, notes: undefined })) })
        : <InlineServiceProofMedia
        items={items}
        galleryItems={items}
        configuration={{ ...configuration, hero: { ...configuration.hero, source: "service_proof" } }}
        locale={base.locale}
        paused={session.pending}
      />}
    </Stack>;
  };
  const noteIds = new Set(
    compiled.snapshot.screens
      .flatMap((item) => item.components)
      .filter((component) => component.type === "technician_notes")
      .map((component) => component.id),
  );
  const currentNotes =
    screen?.components.filter((component) => noteIds.has(component.id)) ?? [];
  const notes = currentNotes.length
    ? currentNotes
    : showRatingSummary
      ? (ratingSource?.components.filter((component) =>
          noteIds.has(component.id),
        ) ?? [])
      : [];
  const completedServiceNotes = [
    ...notes.flatMap((note) => note.type === "notes" ? [{ body: note.body, title: note.title }] : []),
    response.runtimeContext?.technicianNotes ?? "",
    ...proofs.flatMap((proof) => proof.type === "proofOfServiceMedia" ? proof.items
      .filter((item) => item.notes && surveyHtmlText(item.notes).trim() !== surveyHtmlText(item.label).trim())
      .map((item) => ({ body: item.notes ?? "", title: item.label })) : []),
  ];
  const hasCompletedServiceSummary = completedServiceNotes.some(note =>
    surveyHtmlText(typeof note === "string" ? note : note.body).trim() !== ""
  );
  const proofItems = proof?.type === "proofOfServiceMedia" ? proof.items : [];
  const heroItems = (
    configuration.hero.source === "branding"
      ? configuration.branding.logoUrl
        ? [
            {
              type: "image" as const,
              url: configuration.branding.logoUrl,
              label: flow.brand.companyName,
            },
          ]
        : []
      : proofItems
  ).filter(
    (item) =>
      item.type !== "note" &&
      configuration.hero.mediaTypes.includes(
        item.type === "voice" ? "audio" : item.type,
      ),
  );
  const normalProofHeroAspectRatio =
    configuration.hero.source === "service_proof" &&
    Math.abs(configuration.hero.aspectRatio - 393 / 497) < 0.0001
      ? 393 / 452
      : configuration.hero.aspectRatio;
  const compactProofGeometry = useMemo(
    () => ({
      contentWidth: configuration.layout.contentWidth,
      headerHeight: configuration.header.logoSize + 28,
      heroAspectRatio: normalProofHeroAspectRatio,
      overlap: configuration.layout.overlap,
      railPossible: configuration.hero.showProgress && heroItems.length > 1,
      ratingFoldReserve: hasCompletedServiceSummary ? 284 : 184,
    }),
    [
      configuration.header.logoSize,
      configuration.hero.showProgress,
      configuration.hero.source,
      configuration.layout.contentWidth,
      configuration.layout.overlap,
      hasCompletedServiceSummary,
      heroItems.length,
      normalProofHeroAspectRatio,
    ],
  );
  const compactProofHero = useCompactProofHero(
    configuration.hero.source === "service_proof" && heroItems.length > 0,
    compactProofGeometry,
  );
  const shrinkProofHero = compactProofHero && normalProofHeroAspectRatio < 393 / 360;
  const hero = heroItems.length ? (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor:
          configuration.hero.source === "branding"
            ? configuration.tokens.surface
            : undefined,
      }}
    >
      <FeedbackShellMedia
        items={heroItems}
        galleryItems={proofItems.filter((item) => item.type !== "note")}
        onMinimumHeightChange={setMinimumMediaHeight}
        configuration={configuration}
        locale={base.locale}
        paused={session.pending}
        bottomInset={configuration.layout.overlap}
        compact={compactProofHero}
        shrinkToCompactFrame={shrinkProofHero}
      />
    </Box>
  ) : undefined;
  const usesRetiredReviewHeading = Boolean(
    screen?.components.some(
      (component) => component.type === "reviewSelection",
    ) &&
    screen.components.some(
      (component) =>
        component.type === "h1" &&
        surveyHtmlText(component.text).trim() === retiredReviewHeading,
    ) &&
    screen.components.some(
      (component) =>
        component.type === "subheader" &&
        surveyHtmlText(component.text).trim() === retainedReviewPrompt,
    ),
  );
  const hasReviewAndTip = !!screen?.components.some((c) => c.type === "reviewSelection") && !!tippingComponent;
  const tipFirstReviewActive = tipFirstReviewEnabled && hasReviewAndTip &&
    (showRatingSummary || policy.preview || response.testMode === true) && !driver.revisit;
  const reviewIsRevealed = Boolean(screenId && reviewRevealed[screenId]) ||
    confirmedTips.some(({ component }) => component.id === tippingComponent?.id);
  useEffect(() => {
    if (tipFirstReviewActive && reviewIsRevealed && reviewRevealed[screenId]) {
      revealedReviewRef.current?.focus();
    }
  }, [tipFirstReviewActive, reviewIsRevealed, reviewRevealed, screenId, session.visitId, session.pending]);
  const authoredCardComponents =
    screen?.components.filter(
      (component) =>
        component.type !== "topBar" &&
        (!tipFirstReviewActive || reviewIsRevealed || component.type !== "reviewSelection") &&
        !(
          usesRetiredReviewHeading &&
          component.type === "h1" &&
          surveyHtmlText(component.text).trim() === retiredReviewHeading
        ) &&
        (!(ratingComponent || (tippingComponent && showRatingSummary && !hasReviewAndTip)) ||
          !["h1", "subheader"].includes(component.type)) &&
        (!proofs.some((proof) => proof.id === component.id) || inlineProofIds.has(component.id)) &&
        !notes.some((note) => note.id === component.id) &&
        !(hideTippingBack && component.type === "backButton") &&
        !redundantTippingNavigationIds.has(component.id) &&
        (!driver.revisit || component.id === driver.revisit.tipComponentId),
    ) ?? [];
  const cardComponents = tipFirstReviewActive && reviewIsRevealed
    ? [...authoredCardComponents.filter((component) => component.type !== 'tipping'),
       ...authoredCardComponents.filter((component) => component.type === 'tipping')]
    : authoredCardComponents;
  const tipIndex = cardComponents.findIndex((c) => c.type === "tipping" || c.type === "finixTipping");
  let tipStart = tipIndex;
  if (hasReviewAndTip && tipIndex > 0) {
    while (tipStart > 0 && ["h1", "subheader"].includes(cardComponents[tipStart - 1].type)) tipStart--;
  }
  const initialCardGroups = (hasReviewAndTip || ratingComponent) && tipStart > 0
    ? [cardComponents.slice(0, tipStart), cardComponents.slice(tipStart)]
    : [cardComponents];
  const confirmedTipIds = new Set(confirmedTips.map(({ component }) => component.id));
  const completedTipIntroIds = new Set<string>();
  cardComponents.forEach((component, index) => {
    if (!confirmedTipIds.has(component.id)) return;
    for (let prior = index - 1; prior >= 0 && ["h1", "subheader"].includes(cardComponents[prior].type); prior--) {
      completedTipIntroIds.add(cardComponents[prior].id);
    }
  });
  const standaloneReferralGroups = initialCardGroups.flatMap(group => {
    const groups: FlowComponentConfig[][] = [];
    let ordinary: FlowComponentConfig[] = [];
    for (const component of group) {
      const isReferralCard = component.type === "actionButton" && component.action === "referral";
      if (!isReferralCard) { ordinary.push(component); continue; }
      if (ordinary.length) { groups.push(ordinary); ordinary = []; }
      groups.push([component]);
    }
    if (ordinary.length) groups.push(ordinary);
    return groups;
  });
  const proofCardGroups = standaloneReferralGroups.flatMap(group => {
    const groups: FlowComponentConfig[][] = [];
    let ordinary: FlowComponentConfig[] = [];
    for (const component of group) {
      if (!inlineProofIds.has(component.id)) { ordinary.push(component); continue; }
      if (component.type !== "proofOfServiceMedia" || !component.items.some(item => item.type !== "note")) continue;
      if (ordinary.length) { groups.push(ordinary); ordinary = []; }
      groups.push([component]);
    }
    if (ordinary.length) groups.push(ordinary);
    return groups;
  });
  const cardGroups = proofCardGroups.map(group => {
    // Keep the paid widget's retry action available if advancing failed.
    if (session.pending || session.error || !group.some(component => confirmedTipIds.has(component.id))) return group;
    return group.filter(component => !confirmedTipIds.has(component.id) && !completedTipIntroIds.has(component.id));
  }).filter(group => group.length > 0);
  const completedServiceSummary = (
    <CompletedServiceSummary
      title={configuration.localization.translations[base.locale ?? configuration.localization.defaultLocale]?.["configuration/notes/summaryTitle"] ?? "Completed service summary"}
      notes={completedServiceNotes}
      compact={compactProofHero}
    />
  );
  const inlineProofCardIndex = !isSheet ? cardGroups.findIndex(group => group.some(component =>
    inlineProofIds.has(component.id) && component.type === "proofOfServiceMedia" && component.items.some(item => item.type !== "note")
  )) : -1;
  const footerCardIndex = cardGroups.reduce((last, group, index) => group.some(component => !inlineProofIds.has(component.id)) ? index : last, -1);
  const tipSummaryCanContinue = (componentId: string) => {
    if (!screen || session.error || !screen.components.some(component => component.id === componentId)) return false;
    const ownedIds = authoredScreen?.components.find(component => component.id === componentId)?.actionIds ?? [];
    return !compiled.snapshot.actions.some(action => ownedIds.includes(action.id) && action.type === "tip" && action.destination.kind === "screen" && action.destination.screenId === screen.id);
  };
  const reviewSelection = cardComponents.find(
    (component) => component.type === "reviewSelection",
  );
  // An authored self-loop is the final inline CTA state, not a forward step.
  // Keep Continue for terminal/forward destinations; Edit is sufficient only
  // when continuing would resolve right back to this same review screen.
  const testReviewSelfLoop = (() => {
    if (response.testMode !== true || !summaryEdit || !screen) return false;
    try {
      const next = driver.resolve(screen.id, session.values, session.ratingComponentId).destination;
      return next.kind === "screen" && next.screenId === screen.id;
    } catch {
      return false;
    }
  })();
  const reviewSelectionHasDirectActions = Boolean(
    (response.testMode !== true || testReviewSelfLoop) &&
    reviewSelection?.type === "reviewSelection" &&
    (reviewSelection.sites.length > 0 ||
      (reviewSelection.callbackPhone && reviewSelection.callbackLabel)),
  );
  return (
    <CompanyBrandProvider brand={flow.brand}>
      <Box
        ref={sheetContainer}
        dir={configuration.localization.direction}
        onClickCapture={blockNativeNavigation}
        onAuxClickCapture={blockNativeNavigation}
        onContextMenuCapture={blockNativeNavigation}
        onSubmitCapture={blockNativeNavigation}
        style={
          {
            "--cx-feedback-background": configuration.tokens.background,
            "--cx-feedback-header":
              configuration.header.background === "surface"
                ? configuration.tokens.surface
                : configuration.header.background === "background"
                  ? configuration.tokens.background
                  : configuration.header.background,
            "--cx-feedback-card": `color-mix(in srgb, ${configuration.tokens.surface} ${configuration.theme.surfaceOpacity * 100}%, transparent)`,
          } as React.CSSProperties
        }
      >
        <FeedbackSurveyShell
          hero={hero}
          footer={<PoweredByApruv sx={{ pb: 4 }} />}
          header={header}
          direction={configuration.localization.direction}
          layout={{
            contentWidth: configuration.layout.contentWidth,
            gutter: configuration.layout.gutter,
            overlap: configuration.layout.overlap,
            // Compact the previous default; preserve explicitly authored aspect ratios.
            heroAspectRatio: configuration.hero.source === "service_proof" && shrinkProofHero
              ? 393 / 316
              : configuration.hero.source === "service_proof" &&
                  Math.abs(configuration.hero.aspectRatio - 393 / 497) < 0.0001
                ? 393 / 452
                : configuration.hero.aspectRatio,
            minimumMediaHeight,
            cardRadius: configuration.theme.radius,
            headerHeight: configuration.header.logoSize + 28,
            compactHero: compactProofHero,
          }}
        >
          {screen && isSheet && (
            <FeedbackFlowSheet
              container={() => sheetContainer.current}
              contentWidth={configuration.layout.contentWidth}
              open
              title={
                isFeedbackSheet
                  ? feedbackSheetTitle!
                  : sheetHeading?.type === "h1"
                    ? sheetHeading.text
                    : localizedCopy(
                        "feedback/title",
                        configuration.feedback.title,
                      )
              }
              body={
                isFeedbackSheet
                  ? localizedOverride("feedback/body") === undefined &&
                    sheetSubtitle?.type === "subheader"
                    ? sheetSubtitle.text
                    : localizedCopy(
                        "feedback/body",
                        configuration.feedback.body,
                      )
                  : sheetSubtitle?.type === "subheader"
                    ? sheetSubtitle.text
                    : undefined
              }
              pending={session.pending}
              onDismiss={(() => {
                const back = screen.components.find(
                  (component) => component.type === "backButton",
                );
                return back &&
                  (!driver.usesHistoryBack(screen.id, back.id) ||
                    session.canGoBack)
                  ? () => session.goBack(screen.id, back.id)
                  : undefined;
              })()}
              actions={
                isFeedbackSheet ? (
                  <>
                    {!configuration.feedback.required &&
                      (feedbackSkipButtons.length ? (
                        feedbackSkipButtons.map((button) =>
                          renderBlock(
                            button.type === "actionButton"
                              ? {
                                  ...button,
                                  label: feedbackActionLabel(
                                    "skip",
                                    button.label,
                                    feedbackSkipButtons.length,
                                  ),
                                }
                              : button,
                            true,
                          ),
                        )
                      ) : (
                        <Button
                          disabled={session.pending}
                          onClick={() =>
                            session.submitFeedback(screen.id, true)
                          }
                          sx={{
                            bgcolor: "rgba(145, 158, 171, 0.16)",
                            "&:hover": { bgcolor: "rgba(145, 158, 171, 0.24)" },
                            borderRadius: "8px",
                            flex: 1,
                            minHeight: 48,
                            textTransform: "none",
                            fontSize: 15,
                            lineHeight: "26px",
                            fontWeight: 600,
                            color: "var(--cx-brand-text)",
                          }}
                        >
                          {feedbackActionLabel(
                            "skip",
                            resolveSurveyTemplate(
                              configuration.feedback.skipLabel,
                              response,
                              configuration.branding,
                              configuration.content.variableFallbacks,
                            ),
                            1,
                          )}
                        </Button>
                      ))}
                    {feedbackSubmitButtons.length ? (
                      feedbackSubmitButtons.map((button) =>
                        renderBlock(
                          button.type === "actionButton"
                            ? {
                                ...button,
                                label: feedbackActionLabel(
                                  "submit",
                                  button.label,
                                  feedbackSubmitButtons.length,
                                ),
                              }
                            : button,
                          false,
                        ),
                      )
                    ) : (
                      <Button
                        disabled={session.pending}
                        variant="contained"
                        disableElevation
                        onClick={() =>
                          session.submitFeedback(
                            screen.id,
                            false,
                            feedbackForms[0]?.id,
                          )
                        }
                        sx={{
                          flex: 1,
                          minHeight: 48,
                          textTransform: "none",
                          fontSize: 15,
                          lineHeight: "26px",
                          fontWeight: 600,
                          bgcolor: "var(--cx-brand-primary)",
                          color: "var(--cx-brand-primary-contrast)",
                        }}
                      >
                        {feedbackActionLabel(
                          "submit",
                          resolveSurveyTemplate(
                            configuration.feedback.submitLabel,
                            response,
                            configuration.branding,
                            configuration.content.variableFallbacks,
                          ),
                          1,
                        )}
                      </Button>
                    )}
                  </>
                ) : !screen.hideFooterAction ? (
                  <Button
                    disabled={session.pending}
                    variant="contained"
                    onClick={() => session.interact(screen.id)}
                    sx={{
                      flex: 1,
                      minHeight: 48,
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: "var(--cx-brand-primary)",
                      color: "var(--cx-brand-primary-contrast)",
                      "&:hover": { bgcolor: "var(--cx-brand-primary-dark)" },
                    }}
                  >
                    {continueLabel}
                  </Button>
                ) : undefined
              }
            >
              {session.pending && (
                <Alert severity="info" role="status">
                  Saving your feedback…
                </Alert>
              )}
              {session.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {session.error}
                </Alert>
              )}
              {isFeedbackSheet ? (
                <>
                  <FeedbackRecoveryFields
                    reasonsLabel={
                      surveyHtmlText(
                        localizedCopy(
                          "feedback/title",
                          configuration.feedback.title,
                        ),
                      ) || surveyHtmlText(feedbackSheetTitle ?? "Feedback")
                    }
                    forms={feedbackForms.map((form) => ({
                      ...form,
                      title:
                        form.title ===
                        localizedCopy(
                          "feedback/title",
                          configuration.feedback.title,
                        )
                          ? undefined
                          : form.title,
                    }))}
                    reasons={configuration.feedback.reasons.map((reason) => ({
                      ...reason,
                      label: localizedCopy(
                        `feedback/reasons/${encodeURIComponent(reason.id)}`,
                        reason.label,
                      ),
                    }))}
                    values={session.values}
                    selection={configuration.feedback.selection}
                    maxLength={configuration.feedback.maxLength}
                    pending={session.pending}
                    onChange={(id, value) =>
                      session.setValue(screen.id, id, value)
                    }
                  />
                  {screen.components
                    .filter(
                      (component) =>
                        component.type !== "feedbackForm" &&
                        component.type !== "topBar" &&
                        !(
                          component.type === "visualAsset" &&
                          component.preset === "negative_feedback" &&
                          !component.url &&
                          !component.animation
                        ) &&
                        component.id !== sheetHeading?.id &&
                        component.id !== sheetSubtitle?.id &&
                        !feedbackActionIds.has(commandKey(component)) &&
                        (!proofs.some((proof) => proof.id === component.id) || inlineProofIds.has(component.id)) &&
                        !notes.some((note) => note.id === component.id),
                    )
                    .map((component) => renderCardBlock(component))}
                </>
              ) : (
                <FeedbackRecoveryContent
                  components={screen.components.filter(
                    (component) =>
                      component.type !== "topBar" &&
                      component.id !== sheetHeading?.id &&
                      component.id !== sheetSubtitle?.id &&
                      (!proofs.some((proof) => proof.id === component.id) || inlineProofIds.has(component.id)) &&
                      !notes.some((note) => note.id === component.id),
                  )}
                  renderBlock={renderCardBlock}
                  componentKey={commandKey}
                  surface={configuration.tokens.background}
                  border={configuration.tokens.border}
                  spacing={configuration.theme.spacing === "compact" ? 1 : 2}
                />
              )}
            </FeedbackFlowSheet>
          )}
          {session.error && !isSheet && (
            <Alert severity="error">{session.error}</Alert>
          )}
          {session.pending && !isSheet && (
            <Alert severity="info">Saving your feedback…</Alert>
          )}
          {revisitMessage ? (
            <FeedbackSurveyCard radius={configuration.theme.radius}>
              <Typography variant="h6">
                {localizedCopy(
                  "revisitPolicy/messageTitle",
                  configuration.revisitPolicy.messageTitle,
                )}
              </Typography>
              <Typography>
                {localizedCopy(
                  "revisitPolicy/messageBody",
                  configuration.revisitPolicy.messageBody,
                )}
              </Typography>
              {driver.revisit?.tipComponentId && (
                <Button onClick={() => setShowRevisitTip(true)}>
                  {localizedCopy("tipping/title", configuration.tipping.title)}
                </Button>
              )}
            </FeedbackSurveyCard>
          ) : terminal ? (
            <FeedbackSurveyCard radius={configuration.theme.radius}>
              <Typography variant="h6">
                {localizedCopy(
                  "completion/title",
                  configuration.completion.title,
                )}
              </Typography>
              <Typography>
                {localizedCopy(
                  "completion/body",
                  configuration.completion.body,
                ) || "This experience is complete."}
              </Typography>
              {(response.testMode || response.persistenceDisabled) && (
                <Alert severity="info" role="status" sx={{ mt: 2 }}>
                  Test mode. Nothing was saved.
                </Alert>
              )}
            </FeedbackSurveyCard>
          ) : screen ? (
            <>
            {!driver.revisit && hasCompletedServiceSummary && inlineProofCardIndex < 0 && <Box sx={{ '&:has(> *)': { mb: compactProofHero || configuration.theme.spacing === "compact" ? 1 : 2 } }}>
              {completedServiceSummary}
            </Box>}
            <fieldset
              disabled={session.pending}
              style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
            >
              <Stack
                spacing={configuration.theme.spacing === "compact" ? 1 : 2}
              >
                {!driver.revisit &&
                  proofs.filter(proof => !inlineProofIds.has(proof.id)).map((proofBlock) => {
                    if (proofBlock.type !== "proofOfServiceMedia") return null;
                    const proof = proofBlock;
                    const consumed =
                      configuration.hero.source === "service_proof"
                        ? heroItems
                        : [];
                    const remaining = proof.items.filter(
                      (item) => item.type !== "note" && !consumed.includes(item),
                    );
                    if (!remaining.length) return null;
                    return (
                      <FeedbackSurveyCard
                        key={proof.id}
                        radius={configuration.theme.radius}
                      >
                        <Stack spacing={1} data-proof-content={proof.id}>
                          {proof.title && (
                            <HtmlText html={proof.title} variant="subtitle2" />
                          )}
                          {remaining.length > 0 &&
                            renderBlock({ ...proof, items: remaining.map((item) => ({ ...item, notes: undefined })) })}

                        </Stack>
                      </FeedbackSurveyCard>
                    );
                  })}
                {!isSheet && cardGroups.map((group, groupIndex) => {
                  const standaloneReferral = group.length === 1 &&
                    group[0].type === "actionButton" &&
                    group[0].action === "referral";
                  return (
                  <React.Fragment key={groupIndex}>
                  {standaloneReferral ? renderCardBlock(group[0]) : <FeedbackSurveyCard radius={configuration.theme.radius}>
                    <Stack
                      spacing={
                        configuration.theme.spacing === "compact" ? 1 : 2
                      }
                    >
                      {screen.presentation === "confirmation_card" ? (
                        <FeedbackConfirmationContent
                          components={group}
                          renderBlock={renderCardBlock}
                          componentKey={commandKey}
                          spacing={
                            configuration.theme.spacing === "compact" ? 1 : 2
                          }
                        />
                      ) : (
                        group.map(renderCardBlock)
                      )}
                      {groupIndex === footerCardIndex && !screen.hideFooterAction &&
                        !(tipFirstReviewActive && !reviewIsRevealed) &&
                        !reviewSelectionHasDirectActions && (
                          <Button
                            variant="contained"
                            onClick={() => session.interact(screen.id)}
                            sx={{
                              minHeight: 48,
                              textTransform: "none",
                              fontWeight: 600,
                              bgcolor: "var(--cx-brand-primary)",
                              color: "var(--cx-brand-primary-contrast)",
                              "&:hover": {
                                bgcolor: "var(--cx-brand-primary-dark)",
                              },
                            }}
                          >
                            {continueLabel}
                          </Button>
                        )}
                    </Stack>
                  </FeedbackSurveyCard>}
                  {!driver.revisit && hasCompletedServiceSummary && groupIndex === inlineProofCardIndex && (
                    <Box>
                      {completedServiceSummary}
                    </Box>
                  )}
                  </React.Fragment>
                  );
                })}
                {confirmedTips.map(({ component, amount, paymentCopy }) =>
                  tipSummaryCanContinue(component.id) ? (
                    <Button key={component.id} disabled={session.pending} onClick={() => session.interact(screen.id, component.id, { componentId: component.id, type: "tip", value: amount })}>
                      {paymentCopy?.["payment.continue"] || "Continue"}
                    </Button>
                  ) : null,
                )}
                {showRatingSummary && (
                  <FeedbackAcknowledgedSummary
                    kind="rating"
                    badge={ratingSummaryBadge}
                    radius={configuration.theme.radius}
                    title={ratingSummaryTitle}
                    body={ratingSummaryBody}
                    editLabel={ratingSummaryEditLabel}
                    onEdit={
                      summaryEdit
                        ? () => session.editSummary(screen.id)
                        : undefined
                    }
                    disabled={session.pending}
                  />
                )}
                {confirmedTips.map(({ component, amount, paymentCopy, paymentLocale }) => (
                  <React.Fragment key={component.id}>
                  <FeedbackAcknowledgedSummary
                    kind="tip"
                    badge={paymentCurrency(
                      amount,
                      paymentLocale,
                      true,
                    )}
                    radius={configuration.theme.radius}
                    title={
                      paymentCopy?.["payment.summaryTitle"] ||
                      "Tip submitted"
                    }
                    body={policy.preview
                      ? "Simulated tip — no payment was collected."
                      : paymentCopy?.["payment.summaryBody"] || paymentCopy?.["payment.confirmed"] || "Your tip is confirmed. Thank you!"}
                  />
                  </React.Fragment>
                ))}
              </Stack>
            </fieldset>
            </>
          ) : (
            <Alert severity="error">This survey screen is unavailable.</Alert>
          )}
        </FeedbackSurveyShell>
      </Box>
    </CompanyBrandProvider>
  );
}
